"use client";

import React from "react";
import type { HouseData, PlanetPosition } from "@/lib/api/kundali";
import { resolveRashi } from "@/lib/rashi-presentation";
import { GRAHA_ABBREVIATIONS, type ChartLocale } from "@ishvaram/ui/Jyotish";
import { kundaliRegionalLocale } from "@/i18n/regional-locales";
import { regionalRashiNameByNumber } from "@/lib/jyotish-regional-terms";
import { REGIONAL_RESULT_COPY } from "./kundali/regional-result-copy";

/** The fields the chart reads, so divisional charts can pass their own rows. */
export type ChartPlanet = Pick<PlanetPosition, "planet_id" | "rashi"> &
  Partial<Pick<PlanetPosition, "house" | "is_retrograde" | "is_exalted" | "is_debilitated">>;
export type ChartHouse = Pick<HouseData, "house_number" | "sign">;

export interface SouthIndianKundaliChartProps {
  planets: readonly ChartPlanet[];
  houses?: readonly ChartHouse[];
  locale?: ChartLocale;
}

// South Indian 12-box fixed zodiac layout:
// Grid 4x4 with center 2x2 empty
// Row 1: Pisces (12), Aries (1), Taurus (2), Gemini (3)
// Row 2: Aquarius (11), [EMPTY], [EMPTY], Cancer (4)
// Row 3: Capricorn (10), [EMPTY], [EMPTY], Leo (5)
// Row 4: Sagittarius (9), Scorpio (8), Libra (7), Virgo (6)

const SIGN_NAMES_EN = [
  "",
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

const SIGN_NAMES_HI = [
  "",
  "मेष",
  "वृषभ",
  "मिथुन",
  "कर्क",
  "सिंह",
  "कन्या",
  "तुला",
  "वृश्चिक",
  "धनु",
  "मकर",
  "कुंभ",
  "मीन",
];

// Chart lines are Jyotish geometry (DESIGN.md: indigo geometry), not UI
// borders — they take the primary geometry color, not a border-* role token.
const CHART_FRAME_STYLE = { borderColor: "var(--color-primary)" };
const CHART_LINE_STYLE = {
  borderColor: "color-mix(in srgb, var(--color-primary) 75%, transparent)",
};
const CENTER_BOX_STYLE = {
  borderColor: "color-mix(in srgb, var(--color-primary) 40%, transparent)",
};

const BOX_LAYOUT = [
  { signNum: 12, row: 1, col: 1 },
  { signNum: 1, row: 1, col: 2 },
  { signNum: 2, row: 1, col: 3 },
  { signNum: 3, row: 1, col: 4 },
  { signNum: 11, row: 2, col: 1 },
  { signNum: 4, row: 2, col: 4 },
  { signNum: 10, row: 3, col: 1 },
  { signNum: 5, row: 3, col: 4 },
  { signNum: 9, row: 4, col: 1 },
  { signNum: 8, row: 4, col: 2 },
  { signNum: 7, row: 4, col: 3 },
  { signNum: 6, row: 4, col: 4 },
];

/** Labels, sign names and graha marks in the chart's locale. */
function southChartCopy(locale: ChartLocale) {
  const regional = kundaliRegionalLocale(locale);
  if (regional) {
    const copy = REGIONAL_RESULT_COPY[regional].southChart;
    return {
      ...copy,
      signName: (signNumber: number) =>
        regionalRashiNameByNumber(signNumber, regional) ?? SIGN_NAMES_EN[signNumber],
      // The North Indian chart's graha marks.
      planetMark: (planetId: string) =>
        GRAHA_ABBREVIATIONS[regional][planetId.toLowerCase()] ?? planetId.slice(0, 2),
    };
  }
  const isHi = locale === "hi";
  return {
    label: isHi ? "दक्षिण भारतीय कुंडली चक्र" : "South Indian Vedic Birth Chart",
    centerTitle: isHi ? "दक्षिण भारतीय चक्र" : "South Indian Chart",
    centerSubtitle: isHi ? "स्थिर राशि मंडल" : "Fixed Zodiac Matrix",
    signName: (signNumber: number) => (isHi ? SIGN_NAMES_HI : SIGN_NAMES_EN)[signNumber],
    // en and hi keep the two-letter Latin id this chart has always drawn.
    planetMark: (planetId: string) => planetId.slice(0, 2),
  };
}

export default function SouthIndianKundaliChart({
  planets,
  houses = [],
  locale = "en",
}: SouthIndianKundaliChartProps) {
  const copy = southChartCopy(locale);
  const lagnaSignNumber = resolveRashi(
    houses.find((house) => house.house_number === 1)?.sign
  )?.number;

  // Map planets to sign numbers (1-12)
  const planetsBySign: Record<number, ChartPlanet[]> = {};
  for (let i = 1; i <= 12; i++) planetsBySign[i] = [];

  planets.forEach((p) => {
    const signNumber = resolveRashi(p.rashi)?.number;
    if (signNumber && planetsBySign[signNumber]) {
      planetsBySign[signNumber]!.push(p);
    }
  });

  return (
    <div
      className="w-full max-w-md mx-auto aspect-square rounded-2xl border-2 bg-surface-canvas p-2 grid grid-cols-4 grid-rows-4 gap-1 select-none"
      style={CHART_FRAME_STYLE}
      aria-label={copy.label}
    >
      {/* 12 Outer Boxes */}
      {BOX_LAYOUT.map((box) => {
        const signPlanets = planetsBySign[box.signNum] || [];
        const signName = copy.signName(box.signNum);
        const isLagnaBox = box.signNum === lagnaSignNumber;

        const boxStyle = { ...CHART_LINE_STYLE, gridRow: box.row, gridColumn: box.col };

        return (
          <div
            key={box.signNum}
            data-testid={`south-chart-sign-${box.signNum}`}
            className={`border rounded-lg p-1.5 flex flex-col justify-between overflow-hidden relative ${
              isLagnaBox ? "bg-surface-brand-subtle" : "bg-surface-raised/80"
            }`}
            style={boxStyle}
          >
            {/* Sign Label */}
            <div className="flex justify-between items-center text-caption font-bold text-text-muted">
              <span>{signName}</span>
              <span className="font-mono text-caption">{box.signNum}</span>
            </div>

            {/* Planets in this Sign */}
            <div className="flex flex-wrap gap-1 my-auto">
              {signPlanets.map((p, idx) => (
                <span
                  key={idx}
                  className={`text-caption font-bold font-mono px-1 py-0.2 rounded ${"text-text-primary bg-surface-warm"}`}
                >
                  {copy.planetMark(p.planet_id)}
                  {p.is_retrograde ? "®" : ""}
                </span>
              ))}
            </div>
          </div>
        );
      })}

      {/* Center 2x2 Box */}
      <div
        className="col-span-2 row-span-2 col-start-2 row-start-2 border rounded-xl bg-surface-canvas flex flex-col items-center justify-center p-3 text-center"
        style={CENTER_BOX_STYLE}
      >
        <span className="text-xl mb-0.5">☸️</span>
        <span className="font-serif text-xs font-bold text-text-primary">{copy.centerTitle}</span>
        <span className="text-caption text-text-muted mt-0.5">{copy.centerSubtitle}</span>
      </div>
    </div>
  );
}
